import { memo, useMemo } from 'react';
import { Badge } from '@monksflow/monks-ui';
import { Building2Icon, ShieldIcon, UsersIcon } from 'lucide-react';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { useAuth } from '@/entities/user';
import type {
  SingleDetailItemProps,
  KnowledgeBaseDetailInfoProps,
} from './KnowledgeBaseDetailInfo.types';
import { useTranslation } from 'react-i18next';

const SingleDetailItem = memo(({ label, children }: SingleDetailItemProps) => {
  return (
    <div className="flex flex-1 flex-col gap-2 rounded-md border border-border px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      {children}
    </div>
  );
});

SingleDetailItem.displayName = 'SingleDetailItem';

const getVisibilityIcon = (isInternal?: boolean, workspaceId?: string) => {
  if (isInternal) return <ShieldIcon className="size-4" />;
  if (workspaceId) return <UsersIcon className="size-4" />;
  return <Building2Icon className="size-4" />;
};

export const KnowledgeBaseDetailInfo = memo(({ knowledgeBase }: KnowledgeBaseDetailInfoProps) => {
  const { t } = useTranslation('knowledge');

  const organizationName = useAuth((state) => state.signedInUser.value?.organization.name) || '';
  const workspaces = useAuth((state) => state.signedInUser.value?.workspaces);
  const workspaceName = useMemo(
    () => workspaces?.find((w) => w.id === knowledgeBase.workspaceId)?.name || '',
    [workspaces, knowledgeBase.workspaceId],
  );

  return (
    <div className="flex w-full gap-4">
      {knowledgeBase.createdByUsername && (
        <SingleDetailItem label={t('knowledgeBases.knowledgeBaseDetailInfo.createdBy')}>
          <p className="text-base font-medium">{knowledgeBase.createdByUsername}</p>
        </SingleDetailItem>
      )}
      <SingleDetailItem label={t('knowledgeBases.knowledgeBaseDetailInfo.lastModified')}>
        <p className="text-base font-medium">
          {knowledgeBase.updated
            ? formatRelativeDate(knowledgeBase.updated)
            : t('knowledgeBases.knowledgeBaseDetailInfo.now')}
        </p>
      </SingleDetailItem>
      <SingleDetailItem label={t('knowledgeBases.knowledgeBaseDetailInfo.spaceVisibility')}>
        <Badge variant="secondary" roundness="full" className="w-fit">
          {getVisibilityIcon(knowledgeBase.isInternal, knowledgeBase.workspaceId)}
          {knowledgeBase.isInternal
            ? t('knowledgeBases.knowledgeBaseDetailInfo.foundation')
            : knowledgeBase.workspaceId
              ? workspaceName
              : organizationName}
        </Badge>
      </SingleDetailItem>
    </div>
  );
});
KnowledgeBaseDetailInfo.displayName = 'KnowledgeBaseDetailInfo';
